/**
 * rt-cart-instant-open.js — abertura otimista do cart drawer (tema Broadcast).
 *
 * O Broadcast só abre o `<cart-drawer>` no evento `theme:product:added`, que dispara depois
 * de: POST /cart/add (~1s) + GET /cart?section_id=api-cart-items — ou seja, ~1,2s após o
 * clique. Este script abre o drawer JÁ NO CLIQUE (usando a própria rotina do Broadcast, para
 * manter scroll-lock/focus/underlay) e mostra um card-esqueleto (shimmer) do produto. Quando
 * o conteúdo real chega (`theme:product:added`), o esqueleto é removido.
 *
 * NÃO toca no theme.js (vendor). Só dispara/ouve eventos públicos do Broadcast.
 */
(function () {
  const SKELETON_ATTR = 'data-rt-skeleton';
  const SAFETY_MS = 8000; // backstop caso nenhum evento de conclusão chegue (ex.: add falhado)
  let safetyTimer = null;

  /** @returns {Element | null} */
  function cartDrawer() {
    return document.getElementById('cart-drawer');
  }

  /** Imagem do produto do botão clicado (card com add direto ou PDP), para o thumbnail do esqueleto. */
  function currentProductImage(btn) {
    // No Broadcast, os media do produto vivem em [data-media-id]; a slide visível é a ativa.
    const sel = '[data-media-id] img, .product__media img, .product-single__media img, media-gallery img';
    // card com add direto: a foto é a do próprio card
    const holder = btn.closest('[data-quick-add-holder]');
    const scope = holder || btn.closest('[data-section-type="product"], .product') || document;
    const imgs = [...scope.querySelectorAll(holder ? 'img' : sel)];
    if (!imgs.length) imgs.push(...document.querySelectorAll(sel));
    const img = imgs.find((i) => i.offsetParent !== null && (i.currentSrc || i.src)) || imgs[0];
    if (!img) return '';

    let src = img.currentSrc || img.src || '';
    // pedir uma versão pequena ao CDN do Shopify (o thumbnail tem ~76px)
    if (/cdn\.shopify|\/cdn\//.test(src)) {
      src = /[?&]width=\d+/.test(src)
        ? src.replace(/([?&])width=\d+/, '$1width=200')
        : src + (src.includes('?') ? '&' : '?') + 'width=200';
    }
    return src;
  }

  /** @param {string} imageUrl */
  function buildSkeleton(imageUrl) {
    const wrap = document.createElement('div');
    wrap.className = 'rt-cart-skeleton';
    wrap.setAttribute(SKELETON_ATTR, '');
    wrap.setAttribute('aria-hidden', 'true');
    const media = imageUrl
      ? `<img class="rt-cart-skeleton__img" src="${imageUrl}" alt="" loading="eager" decoding="async">`
      : '';
    wrap.innerHTML =
      '<div class="rt-cart-skeleton__row">' +
      `<div class="rt-cart-skeleton__media">${media}</div>` +
      '<div class="rt-cart-skeleton__lines">' +
      '<span class="rt-cart-skeleton__bar rt-cart-skeleton__bar--title"></span>' +
      '<span class="rt-cart-skeleton__bar rt-cart-skeleton__bar--sub"></span>' +
      '<span class="rt-cart-skeleton__bar rt-cart-skeleton__bar--price"></span>' +
      '</div>' +
      '</div>';
    return wrap;
  }

  function clearSkeleton() {
    if (safetyTimer) {
      clearTimeout(safetyTimer);
      safetyTimer = null;
    }
    document.querySelectorAll(`[${SKELETON_ATTR}]`).forEach((n) => n.remove());
    document.querySelectorAll('cart-drawer.rt-cart-loading').forEach((d) => d.classList.remove('rt-cart-loading'));
  }

  /**
   * @param {Element} drawer
   * @param {string} imageUrl
   */
  function openWithSkeleton(drawer, imageUrl) {
    // abrir pela rotina do próprio Broadcast (scroll-lock, focus-trap, underlay)
    drawer.dispatchEvent(new CustomEvent('theme:cart-drawer:show', { bubbles: true }));

    const body = drawer.querySelector('[data-cart-drawer-body]') || drawer.querySelector('.drawer__body') || drawer;
    clearSkeleton();
    drawer.classList.add('rt-cart-loading');

    const skeleton = buildSkeleton(imageUrl);
    const holder = drawer.querySelector('[data-items-holder]');
    const head = body.querySelector('.drawer__head');
    if (holder) {
      // carrinho com items → o esqueleto entra no topo da lista
      holder.insertBefore(skeleton, holder.firstChild);
    } else if (head && head.parentElement) {
      // carrinho vazio → logo a seguir ao cabeçalho do drawer (o estado vazio é escondido por CSS)
      head.parentElement.insertBefore(skeleton, head.nextSibling);
    } else {
      body.insertBefore(skeleton, body.firstChild);
    }

    safetyTimer = setTimeout(clearSkeleton, SAFETY_MS);
  }

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const btn = target.closest('button[name="add"], [data-add-to-cart]');
      if (!btn) return;
      // dentro do quick-add deixamos a base tratar (o Broadcast fecha o cart drawer ao abrir o quick-add);
      // as recomendações do drawer adicionam com o drawer já aberto, sem esqueleto
      if (target.closest('.quick-add, [data-quick-add], .popup-quick-add, quick-add-modal, .product-quick-add, [data-quick-add-modal], .rt-cart-recs')) return;
      if (/** @type {HTMLButtonElement} */ (btn).disabled) return;

      const form = btn.closest('form');
      if (form && !(/** @type {HTMLFormElement} */ (form).checkValidity())) return;

      const drawer = cartDrawer();
      if (!drawer) return; // sem drawer (modo "ir para o carrinho") → não fazemos nada

      openWithSkeleton(drawer, currentProductImage(btn));
    },
    true // captura: corre antes do handler do Broadcast
  );

  // conteúdo real chegou → remover o esqueleto (o Broadcast já re-renderizou os items)
  const done = () => requestAnimationFrame(clearSkeleton);
  document.addEventListener('theme:product:added', done);
  document.addEventListener('theme:cart:change', done);
  document.addEventListener('theme:cart:refresh', done);

  // add falhado: o erro aparece no formulário, que o drawer aberto no clique tapava
  document.addEventListener('theme:product:add-error', (event) => {
    clearSkeleton();
    const drawer = cartDrawer();
    // erro vindo de dentro do drawer (recomendações) fica à vista lá
    if (!drawer || (event.target instanceof Node && drawer.contains(event.target))) return;
    if (typeof drawer.closeCartDrawer === 'function') drawer.closeCartDrawer();
  });
})();
